/**
 * Catégorie « Repas » de l'écran d'ajout — favoris et récents.
 *
 * Demandée après usage, et absente des specs : le §6.3 ne prévoit que des
 * aliments, des recettes et la saisie libre. Le manque se voyait à l'usage —
 * un petit-déjeuner de cinq lignes identiques tous les matins se ressaisissait
 * cinq fois par jour, alors qu'il est déjà écrit dans le journal de la veille.
 *
 * 🔴 **Un repas n'est pas un objet de référentiel.** C'est une *occurrence*
 * passée, désignée par sa ligne `meal`. Trois conséquences assumées :
 *
 *   - rien à gérer dans la Bibliothèque : on ne crée pas un repas, on en
 *     réutilise un qu'on a déjà mangé ;
 *   - le favori se pose **depuis le journal**, sur le repas lui-même — c'est le
 *     seul endroit où l'on sait ce qu'il contient ;
 *   - recopier écrit des entrées neuves, littéralement identiques aux
 *     anciennes. Le repas source n'est jamais modifié, et la journée d'origine
 *     encore moins.
 *
 * ⚠️ Les récents sont **dédoublonnés par composition** côté serveur : le même
 * petit-déjeuner trente jours de suite donne une proposition, pas trente. Les
 * favoris ne le sont pas — les étoiles sont des choix explicites.
 *
 * ⚠️ Le repas **en cours de remplissage** est retiré des deux listes : se
 * recopier dans soi-même doublerait chaque ligne. Le serveur refuse déjà le cas
 * (400 `same_meal`), mais un bouton qui ne peut qu'échouer n'a rien à faire à
 * l'écran.
 */

import { useQuery } from '@tanstack/react-query';

import type { SavedMealDTO } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchQuickAccess, QUICK_ACCESS_KEY } from '../../api/foods';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { formatDayRow } from '../progress/weightFields';
import { Section } from './QuickAccessPanel';

function g(value: number): string {
  return (Math.round(value * 10) / 10).toLocaleString('fr-FR');
}

function MealRow({ meal, onPick }: { meal: SavedMealDTO; onPick: () => void }) {
  return (
    <button type="button" onClick={onPick} className="n-pick">
      <span className="n-pick__main">
        <span className="n-row" style={{ alignItems: 'baseline', gap: 8 }}>
          <span className="n-pick__name" style={{ flex: 1 }}>
            {meal.name}
          </span>
          <span className="n-pick__meta" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {formatDayRow(meal.date)}
          </span>
        </span>

        {/*
          Le contenu, pas seulement le nom : deux « Déjeuner » ne se distinguent
          que par là. C'est l'information qui fait choisir.
        */}
        <span className="n-pick__meta n-ellipsis">{meal.items.join(' · ')}</span>

        <span className="n-pick__meta tnum" style={{ whiteSpace: 'nowrap' }}>
          {g(meal.macros.protein)} P · {g(meal.macros.carbs)} G · {g(meal.macros.fat)} L ·{' '}
          {Math.round(meal.macros.kcal).toLocaleString('fr-FR')} kcal
        </span>
      </span>
    </button>
  );
}

export function MealPanel({
  currentMealId,
  onPick,
}: {
  /** Repas en cours de remplissage — retiré des listes. */
  currentMealId: number | undefined;
  onPick: (sourceMealId: number) => void;
}) {
  const quick = useQuery({ queryKey: QUICK_ACCESS_KEY, queryFn: fetchQuickAccess });

  // 🔴 Les données d'abord : une erreur de TRANSPORT ne doit pas masquer un
  // cache valide (V2 §3.5). Voir `readError` dans `api/client.ts`.
  const lecture = readError(quick);
  if (lecture) return <ReadNote error={lecture} quoi="la liste des repas" />;
  if (quick.data === undefined) return <Hint>Chargement…</Hint>;

  const usable = (list: SavedMealDTO[]) => list.filter((meal) => meal.id !== currentMealId);
  const favorites = usable(quick.data.favoriteMeals);
  const favoriteIds = new Set(favorites.map((meal) => meal.id));
  const recents = usable(quick.data.recentMeals).filter((meal) => !favoriteIds.has(meal.id));

  if (favorites.length === 0 && recents.length === 0) {
    return (
      <Hint>
        Aucun repas à réutiliser pour l’instant. Dès qu’un repas contient des entrées, il est
        proposé ici — et le menu « ⋯ » d’un repas permet de le mettre en favori pour le garder
        sous la main.
      </Hint>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
      {favorites.length > 0 && (
        <Section title="Favoris">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {favorites.map((meal) => (
              <MealRow
                key={meal.id}
                meal={meal}
                onPick={() => {
                  onPick(meal.id);
                }}
              />
            ))}
          </div>
        </Section>
      )}

      {recents.length > 0 && (
        <Section title="Récents">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {recents.map((meal) => (
              <MealRow
                key={meal.id}
                meal={meal}
                onPick={() => {
                  onPick(meal.id);
                }}
              />
            ))}
          </div>
        </Section>
      )}

      <Hint>
        Ajouter un repas recopie ses lignes telles qu’elles ont été enregistrées. Elles restent
        corrigeables une à une ensuite, et le repas d’origine n’est pas touché.
      </Hint>
    </div>
  );
}
